/**
 * LeaveApprovalService
 * Line approver inbox and HR final approval queue for the leave approval chain.
 * Route: api/leave-approvals (any signed-in user; access depends on the data the caller is linked to,
 * `hr-queue` is HRAdmin only).
 *
 * Every action returns the refreshed inbox item. A refusal comes back as HTTP 400, 401, 403, 404 or 409
 * with a ready-to-show SA English message (thrown by the HttpClient as a CCMSError), e.g.
 * 409 "Waiting for line approval by {names}." or 409 when an ask is already open.
 */
import { BaseService } from '../base/BaseService';
export class LeaveApprovalService extends BaseService {
    constructor(http, config) {
        const internalConfig = {
            servicePath: config?.servicePath ?? LeaveApprovalService.DEFAULT_SERVICE_PATH,
            basePath: LeaveApprovalService.BASE_PATH,
            endpoints: config?.endpoints,
        };
        super(http, internalConfig);
    }
    // ==================== CAPABILITIES ====================
    /**
     * What the caller can do and their badge counts (cached 5 minutes per user on the server).
     * GET api/leave-approvals/me/capabilities
     */
    async getCapabilities() {
        const response = await this.getCapabilitiesRaw();
        if (!response.data) {
            throw new Error('Failed to read leave approval capabilities');
        }
        return response.data;
    }
    getCapabilitiesRaw() {
        const endpoint = this.getEndpoint('capabilities', 'me/capabilities');
        return this.http.get(this.buildUrl(endpoint));
    }
    // ==================== LINE APPROVER INBOX ====================
    /**
     * Line steps the caller is an active approver of. `pending` (default) = step Pending (1);
     * `waiting-on-employee` = sent back to the employee (5). The caller's own leaves are never listed.
     * List rows carry empty applicantDepartments, balances and colleaguesOnLeave.
     * GET api/leave-approvals/pending?tab=pending|waiting-on-employee
     */
    async getPending(tab = 'pending') {
        const response = await this.getPendingRaw(tab);
        return response.data ?? [];
    }
    getPendingRaw(tab = 'pending') {
        const endpoint = this.getEndpoint('pending', 'pending');
        return this.http.get(this.buildUrlWithParams(endpoint, { tab }));
    }
    /**
     * Leaves the caller acted on, latest action first. pageSize max 100.
     * GET api/leave-approvals/history?page=1&pageSize=20
     */
    async getHistory(page = 1, pageSize = 20) {
        const response = await this.getHistoryRaw(page, pageSize);
        if (!response.data) {
            throw new Error('Failed to read leave approval history');
        }
        return response.data;
    }
    getHistoryRaw(page = 1, pageSize = 20) {
        const endpoint = this.getEndpoint('history', 'history');
        return this.http.get(this.buildUrlWithParams(endpoint, { page, pageSize }));
    }
    // ==================== HR FINAL APPROVAL QUEUE ====================
    /**
     * Pending leaves whose current HR step is Pending (1) or Waiting on employee (5); filter on
     * `chain.currentStepStatusID` if needed. Your own leave comes back with canApprove=false and a
     * notActionableReason unless you are the sole HR admin. HRAdmin only.
     * GET api/leave-approvals/hr-queue
     */
    async getHrQueue() {
        const response = await this.getHrQueueRaw();
        return response.data ?? [];
    }
    getHrQueueRaw() {
        const endpoint = this.getEndpoint('hr-queue', 'hr-queue');
        return this.http.get(this.buildUrl(endpoint));
    }
    // ==================== DETAIL ====================
    /**
     * One leave with its chain and context (departments, balances, colleagues on leave, duty cover).
     * 404 unless the caller is or was in the line pool, acted on a step, asked, or is HRAdmin.
     * GET api/leave-approvals/{leaveId}
     */
    async getItem(leaveId) {
        const response = await this.getItemRaw(leaveId);
        if (!response.data) {
            throw new Error(`Leave approval ${leaveId} not found`);
        }
        return response.data;
    }
    getItemRaw(leaveId) {
        const endpoint = this.getEndpoint('item', `${leaveId}`);
        return this.http.get(this.buildUrl(endpoint));
    }
    // ==================== DECISIONS ====================
    /**
     * Line approve for a pool member, final approve for HR at the HR step, or approve and finalise when
     * `finalise=true` (needs pool membership and HRAdmin; never on your own leave).
     * 409 "Waiting for line approval by {names}." while the line step is open.
     * PUT api/leave-approvals/{leaveId}/approve
     */
    async approve(leaveId, request) {
        const response = await this.approveRaw(leaveId, request);
        if (!response.data) {
            throw new Error(`Failed to approve leave ${leaveId}`);
        }
        return response.data;
    }
    approveRaw(leaveId, request) {
        const endpoint = this.getEndpoint('approve', `${leaveId}/approve`);
        return this.http.put(this.buildUrl(endpoint), request ?? {});
    }
    /**
     * Line reject for a live pool member, otherwise HR reject (HRAdmin). A comment is required (400 otherwise).
     * PUT api/leave-approvals/{leaveId}/reject
     */
    async reject(leaveId, request) {
        const response = await this.rejectRaw(leaveId, request);
        if (!response.data) {
            throw new Error(`Failed to reject leave ${leaveId}`);
        }
        return response.data;
    }
    rejectRaw(leaveId, request) {
        const endpoint = this.getEndpoint('reject', `${leaveId}/reject`);
        return this.http.put(this.buildUrl(endpoint), request ?? {});
    }
    // ==================== ASK THE EMPLOYEE ====================
    /**
     * Send the leave back to the employee for duty cover (askTypeID 1) or an answer (2). The current step
     * goes to Waiting on employee (5). 409 if an ask is already open.
     * PUT api/leave-approvals/{leaveId}/ask
     */
    async askEmployee(leaveId, request) {
        const response = await this.askEmployeeRaw(leaveId, request);
        if (!response.data) {
            throw new Error(`Failed to send leave ${leaveId} back to the employee`);
        }
        return response.data;
    }
    askEmployeeRaw(leaveId, request) {
        const endpoint = this.getEndpoint('ask', `${leaveId}/ask`);
        return this.http.put(this.buildUrl(endpoint), request);
    }
    /**
     * Withdraw the open ask; the step goes back to Pending (1).
     * PUT api/leave-approvals/{leaveId}/ask/{askId}/withdraw
     */
    async withdrawAsk(leaveId, askId) {
        const response = await this.withdrawAskRaw(leaveId, askId);
        if (!response.data) {
            throw new Error(`Failed to withdraw the request on leave ${leaveId}`);
        }
        return response.data;
    }
    withdrawAskRaw(leaveId, askId) {
        const endpoint = this.getEndpoint('withdrawAsk', `${leaveId}/ask/${askId}/withdraw`);
        return this.http.put(this.buildUrl(endpoint), {});
    }
}
LeaveApprovalService.DEFAULT_SERVICE_PATH = 'hr';
LeaveApprovalService.BASE_PATH = 'api/leave-approvals';
//# sourceMappingURL=LeaveApprovalService.js.map